//Lib
import { FieldType, ViewModelBase, ApiResult } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";

// App
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import { SaveStore } from "Globals/Stores/Domain";

// Models
import { ArmedForcesProofModel } from "Globals/Models/Domain/FormSections/EmploymentDetails/ArmedForcesProofModel";
import { ArmedForcesProofDTO } from "Globals/Models/DTO/ArmedForcesProofDTO";
import { UserFile } from "Globals/Models/UserFile";

// View models
import EmploymentDetailsViewModel from "./EmploymentDetailsViewModel";

export default class ArmedForcesProofViewModel extends ViewModelBase<ArmedForcesProofModel> {
    private saveStore: SaveStore = StoresInstance.Domain.SaveStore;

    constructor(armedForcesProofModel?: ArmedForcesProofModel) {
        super(armedForcesProofModel ? armedForcesProofModel : new ArmedForcesProofModel());
    }

    @action
    public stash = async (userFile: UserFile, employmentDetails: EmploymentDetailsViewModel): Promise<void> => {
        let valid = await this.isModelValid();

        if (valid) {
            this.model.fileName = userFile.fileName;
            this.model.fileUrl = userFile.fileUrl;
            let addressViewModel = new ArmedForcesProofViewModel(this.model);
            runInAction(() => {
                employmentDetails.armedForcesProofViewModels.push(addressViewModel);
                // Triggers auto save.
                employmentDetails.setDirty("id", true);
            });
        }
    };

    @action
    public saveFromStash = async (employmentDetailId: string): Promise<void> => {
        try {
            this.setIsLoading(true);
            this.saveStore.setIsSaving(true);

            this.model.employmentDetailId = employmentDetailId;

            let dto: ArmedForcesProofDTO = this.model.toDto();
            const apiResult = await this.Post<ArmedForcesProofDTO>(AppUrls.Server.ApplicationForm.Upsert.ArmedForcesProof, dto);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);
                console.info("ArmedForcedProof:Upsert-Successful");
                return Promise.resolve();
            } else {
                console.error("FamilyAddress:Upsert-Error", apiResult.errors);
                return Promise.reject(apiResult.errors);
            }
        } catch (exception) {
            // Set server validation error messages.
            console.log("Could not save from Stash.");
            console.log(exception);
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public delete = async (employmentDetails: EmploymentDetailsViewModel): Promise<void> => {
        try {
            if (!this.model.getValue("employmentDetailId")) {
                runInAction(() => {
                    let index = employmentDetails.armedForcesProofViewModels.findIndex((vm) => vm.getValue("id") == this.model.id);
                    if (index > -1) {
                        employmentDetails.armedForcesProofViewModels.splice(index, 1);
                    }
                    return;
                });
            }

            this.setIsLoading(true);
            this.saveStore.setIsSaving(true);

            this.model.isDeleted = true;

            let upsertArmedForcesProof: ArmedForcesProofDTO = this.model.toDto();
            const apiResult = await this.Post<ArmedForcesProofDTO>(AppUrls.Server.ApplicationForm.Upsert.ArmedForcesProof, upsertArmedForcesProof);

            if (apiResult.wasSuccessful) {
                runInAction(() => {
                    let index = employmentDetails.armedForcesProofViewModels.findIndex((vm) => vm.getValue("id") === apiResult.payload.id);
                    employmentDetails.armedForcesProofViewModels.splice(index, 1);
                });

                this.model.resetModel();
                return Promise.resolve();
            } else {
                console.error("ArmedForcesProof:Delete-Error", apiResult.errors);
                return Promise.reject(apiResult.errors);
            }
        } catch (exception) {
            console.error(exception);
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    public async isFieldValid(fieldName: keyof FieldType<ArmedForcesProofModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
